'use client';

import { CinemarkLink } from '@/components/cinemark-link/cinemark-link';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { AnalyticsPageCategories } from '@/enums/analytics-type';
import { useDataAnalytics } from '@/hooks/analytics/analytics';
import { HeaderLink } from '@/types/header-link';
import { dataTestTransformer } from '@/utils/data-test-helper';
import clsx from 'clsx';
import React, { useState } from 'react';
import mobileNavMenuStyles from './mobile-nav-menu.module.scss';

type MobileNavMenuProps = {
  className: string;
  primaryLinks: HeaderLink[];
  secondaryLinks: HeaderLink[];
};

export const MobileNavMenu = ({ className, primaryLinks, secondaryLinks }: MobileNavMenuProps): React.JSX.Element => {
  const [isOpen, setIsOpen] = useState(false);
  const { trackClick } = useDataAnalytics();

  return (
    <DropdownMenu open={isOpen}
      onOpenChange={setIsOpen}
    >
      <DropdownMenuTrigger asChild>
        <button className={clsx('flex flex-col items-center justify-center w-6 py-1', className)}
          data-test="mobile-nav-menu-toggle"
          onClick={() => trackClick({ linkCategory: 'Mobile Nav', linkName: 'Mobile Nav Toggle' })}
        >
          <span className="sr-only">Open nav menu</span>
          <span className={clsx('bg-cnk-red block transition-all duration-300 ease-in-out h-0.5 rounded-sm',
            isOpen ? 'rotate-45 translate-y-[7px] w-6' : 'w-5')}
          />
          <span className={clsx('bg-cnk-red block transition-all duration-300 ease-in-out w-5 h-0.5 my-1 rounded-sm',
            isOpen ? 'opacity-0' : 'opacity-100')}
          />
          <span className={clsx('bg-cnk-red block transition-all duration-300 ease-in-out h-0.5 rounded-sm',
            isOpen ? '-rotate-45 -translate-y-[7px] w-6' : 'w-5')}
          />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent className={mobileNavMenuStyles.dropdown}>
        <div>
          {primaryLinks.map((item, index) =>
            <DropdownMenuItem key={index}>
              <CinemarkLink className="text-primary text-xl font-bold"
                dataLinkCategory={AnalyticsPageCategories.MAIN_NAV}
                dataLinkName={item.title}
                dataTest={`mobile-nav-menu-link-${dataTestTransformer(item.title)}`}
                href={item.href}
                useAnchor={item.useAnchor}
              >
                {item.title}
              </CinemarkLink>
            </DropdownMenuItem>
          )}
        </div>

        <div>
          {secondaryLinks.map((item, index) =>
            <DropdownMenuItem key={index}>
              <CinemarkLink className={clsx('text-lg font-bold', mobileNavMenuStyles['secondary-link'])}
                dataLinkCategory={AnalyticsPageCategories.SECONDARY_NAV}
                dataLinkName={item.title}
                dataTest={`mobile-nav-menu-link-${dataTestTransformer(item.title)}`}
                href={item.href}
                useAnchor={item.useAnchor}
              >
                {item.title}
              </CinemarkLink>
            </DropdownMenuItem>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
